// src/pages/reports/MasterOutlist/ReportMasterTrTable.tsx
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TableSortLabel,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  ListSubheader,
} from "@mui/material";
import PaginationReports from "../../brands/PaginationReports";
import { exportRetailersAndAccounts_v1, exportRetailersAndAccounts_csv_v1 } from "../api";
import Export from "../../../../src/assests/images/export.svg";
import Sorting from "../../../../src/assests/images/Sorting.svg";
import { getToken } from "../../../utils/utils";
import * as XLSX from "xlsx";

// Columns must match backend header keys for CSV
export interface Option {
  label: string;
  value: string;
}

const columns: Option[] = [
  { label: "User ID", value: "User ID" },
  { label: "PMI Entity Code", value: "PMI Entity Code" },
  { label: "User First Name", value: "User First Name" },
  { label: "User Last Name", value: "User Last Name" },
  { label: "Region", value: "Region" },
  { label: "Contracted Since", value: "Contracted Since" },
  { label: "ASM Name", value: "ASM Name" },
  { label: "Area Code", value: "Area Code" },
  { label: "TSE Name", value: "TSE Name" },
  { label: "Territory Code", value: "Territory Code" },
  { label: "SFA ID", value: "SFA ID" },
  { label: "Outlet Salesforce ID", value: "Outlet Salesforce ID" },
  { label: "User Last Name", value: "User Last Name" },
  { label: "Outlet Name", value: "Outlet Name" },
  { label: "Outlet City", value: "Outlet City" },
  { label: "Current Rental Value", value: "Current Rental Value" },
  { label: "Status", value: "Status" },
  { label: "Last Updated On", value: "Last Updated On" },
];

// Defaults (first 6 like Rewards)
const DEFAULT_VISIBLE = columns.map((c) => c.label).slice(0, 6);
const DEFAULT_SET = new Set<string>(DEFAULT_VISIBLE);

const withDefaultsIfEmpty = (arr: string[]) =>
  arr && arr.length ? arr : [...DEFAULT_VISIBLE];

type Row = Record<string, any>;

interface ReportsMasterTrTableProps {
  startDate?: Date | null; // -> contractedSince_from
  endDate?: Date | null; // -> contractedSince_to
  searchQuery?: string;
  appliedFilters?: {
    region?: string[] | null;
    city?: string[] | null;
    asm_name?: string[] | null;
    area_code?: string[] | null;
    tse_name?: string[] | null;
    territory_code?: string[] | null;
    sfa_id?: string[] | null;
    outlet_salesforce_id?: string[] | null;
    pmi_entity_code?: string[] | string | null;
    outlet_name?: string[] | null;
    status?: string[] | null;

    contract_since_from?: string | null;
    contract_since_to?: string | null;

    backendFilters?: Record<string, any> | null;
    [k: string]: any;
  } | null;
}

// Rewards-style custom arrow for MUI Select
const CustomArrow: React.FC<{ open: boolean; onClick: () => void }> = ({
  open,
  onClick,
}) => (
  <span
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    style={{
      display: "inline-block",
      width: 11,
      right: "10px",
      height: "8px",
      borderRight: "2px solid #000",
      borderBottom: "2px solid #000",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transition: "transform 0.2s ease",
      marginLeft: "20px",
      position: "relative",
      cursor: "pointer",
      pointerEvents: "auto",
    }}
  />
);

const ReportMasterTrTable: React.FC<ReportsMasterTrTableProps> = ({
  startDate,
  endDate,
  searchQuery,
  appliedFilters = null,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);

  // 🔹 rows-per-page made dynamic + dropdown-controlled
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const totalPages = Math.ceil(totalRows / itemsPerPage);

  // --- Rewards-style column picker state (labels drive table + export) ---
  const allColumnsLabels = columns.map((c) => c.label);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE);
  const [tempSelectedColumns, setTempSelectedColumns] =
    useState<string[]>(DEFAULT_VISIBLE);

  const [colSelectOpen, setColSelectOpen] = useState(false);
  const [colSearch, setColSearch] = useState("");
  const colSearchRef = useRef<HTMLInputElement | null>(null);

  const filteredColumns = React.useMemo(() => {
    const q = (colSearch || "").trim().toLowerCase();
    if (!q) return allColumnsLabels;
    return allColumnsLabels.filter((c) => c.toLowerCase().includes(q));
  }, [allColumnsLabels, colSearch]);

  const allFilteredSelected =
    filteredColumns.length > 0 &&
    filteredColumns.every((c) => tempSelectedColumns.includes(c));

  const toggleSelectAllOnFiltered = () => {
    if (allFilteredSelected) {
      // Deselect only non-defaults in the filtered set
      setTempSelectedColumns((prev) => {
        const next = prev.filter(
          (p) => !filteredColumns.includes(p) || DEFAULT_SET.has(p)
        );
        // Ensure defaults are present (extra safety)
        DEFAULT_VISIBLE.forEach((d) => {
          if (!next.includes(d)) next.push(d);
        });
        return next;
      });
    } else {
      // Select all filtered + keep defaults selected
      setTempSelectedColumns((prev) =>
        Array.from(
          new Set<string>([...prev, ...filteredColumns, ...DEFAULT_VISIBLE])
        )
      );
    }
  };

  // default sort
  const [orderBy, setOrderBy] = useState<string>("User ID");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  const [exporting, setExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const formatDate = (d?: Date | null) => {
    if (!d) return undefined;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // Build payload to match new Master backend
  const buildFlatPayload = (opts?: {
    page?: number;
    limit?: number | "all";
    forExport?: boolean;
  }) => {
    const payload: any = {
      page: opts?.page ?? page,
      limit:
        typeof opts?.limit !== "undefined" ? opts.limit : itemsPerPage, // 🔹 use dynamic page size
      ...(searchQuery ? { search: searchQuery } : {}),
      ...(startDate ? { contractedSince_from: formatDate(startDate) } : {}),
      ...(endDate ? { contractedSince_to: formatDate(endDate) } : {}),
    };

    if (appliedFilters) {
      const {
        region,
        city,
        asm_name,
        area_code,
        tse_name,
        territory_code,
        sfa_id,
        outlet_salesforce_id,
        pmi_entity_code,
        outlet_name,
        status,
        contract_since_from,
        contract_since_to,
      } = appliedFilters;

      if (region?.length) {
        const r = region.join(",");
        payload.region = r; // list endpoint
        payload.regions = r; // CSV/export endpoint
      }
      if (asm_name?.length) payload.asm_name = asm_name.join(",");
      if (area_code?.length) payload.area_code = area_code.join(",");
      if (tse_name?.length) payload.tse_name = tse_name.join(",");
      if (territory_code?.length) payload.territory_code = territory_code.join(",");
      if (sfa_id?.length) payload.sfa_id = sfa_id.join(",");
      if (outlet_salesforce_id?.length)
        payload.outlet_salesforce_id = outlet_salesforce_id.join(",");

      if (Array.isArray(pmi_entity_code) && pmi_entity_code.length)
        payload.pmi_entity_code = pmi_entity_code.join(",");
      else if (typeof pmi_entity_code === "string" && pmi_entity_code)
        payload.pmi_entity_code = pmi_entity_code;

      if (outlet_name?.length) payload.outlet_name = outlet_name.join(",");
      if (city?.length) payload.city = city.join(",");

      if (status?.length) payload.status = status.join(",");

      if (contract_since_from) payload.contractedSince_from = contract_since_from;
      if (contract_since_to) payload.contractedSince_to = contract_since_to;
    }

    if (orderBy === "User ID") {
      payload.sort_dir = order; // "asc" | "desc"
    }

    if (opts?.forExport || String(opts?.limit || "").toLowerCase() === "all") {
      payload.limit = "all";
      payload.export = true;
    }

    return payload;
  };

  // Fetch list
  useEffect(() => {
    const run = async () => {
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch {}
      }
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      setIsLoading(true);
      try {
        const token = getToken();
        const payload = buildFlatPayload();
        const resp = await exportRetailersAndAccounts_v1(
          payload,
          token,
          false,
          ctrl.signal
        );
        const body = resp && (resp as any).data ? (resp as any).data : resp;

        const resultsArray = body?.data?.results ?? body?.results ?? body?.data ?? [];
        const totalRowsValue =
          body?.data?.total_rows ?? body?.total_rows ?? body?.count ?? 0;

        // Normalize to table headers (prefer new BE keys)
        const normalized = (Array.isArray(resultsArray) ? resultsArray : []).map(
          (r: any) => ({
            Region: r.region ?? r["Region"] ?? "",
            "ASM Name": r.asm_name ?? r["ASM Name"] ?? "",
            "Area Code": r.area_code ?? r["Area Code"] ?? "",
            "TSE Name": r.tse_name ?? r["TSE Name"] ?? "",
            "Territory Code": r.territory_code ?? r["Territory Code"] ?? "",
            "SFA ID": r.sfa_id ?? r["SFA ID"] ?? "",
            "Outlet Salesforce ID":
              r.outlet_salesforce_id ?? r["Outlet Salesforce ID"] ?? "",
            "PMI Entity Code": r.pmi_entity_code ?? r["PMI Entity Code"] ?? "",
            "User ID": r.user_id ?? r["User ID"] ?? "",
            "User First Name":
              r.user_first_name ?? r["User First Name"] ?? "",
            "User Last Name": r.user_last_name ?? r["User Last Name"] ?? "",
            "Outlet Name": r.outlet_name ?? r["Outlet Name"] ?? "",
            "Outlet City": r.city ?? r["Outlet City"] ?? "",
            "Contracted Since":
              r.contracted_since ?? r["Contracted Since"] ?? "",
            "Current Rental Value":
              r.current_rental_value ?? r["Current Rental Value"] ?? "",
            Status: r.status ?? r["Status"] ?? "",
            "Last Updated On":
              r.last_updated_on ?? r["Last Updated On"] ?? r.last_modified_date ?? "",
          })
        );

        if (!ctrl.signal.aborted) {
          setRows(normalized);
          setTotalRows(Number(totalRowsValue) || normalized.length);
        }
      } catch (err: any) {
        if (err?.name !== "AbortError") {
          console.error("Failed to fetch master outlet list", err);
          setRows([]);
          setTotalRows(0);
        }
      } finally {
        if (abortRef.current === ctrl) abortRef.current = null;
        setIsLoading(false);
      }
    };

    run();

    return () => {
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch {}
        abortRef.current = null;
      }
    };
  }, [
    startDate,
    endDate,
    searchQuery,
    page,
    appliedFilters,
    order,
    orderBy,
    itemsPerPage, // 🔹 refetch when size changes
  ]);

  // Sort (client-side for current page view)
  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedRows = [...rows].sort((a, b) => {
    const aVal = a[orderBy] ?? "";
    const bVal = b[orderBy] ?? "";
    const dateLike =
      orderBy.toLowerCase().includes("last") ||
      orderBy.toLowerCase().includes("since") ||
      orderBy.toLowerCase().includes("date");
    if (dateLike) {
      const ad = aVal ? new Date(aVal).getTime() : 0;
      const bd = bVal ? new Date(bVal).getTime() : 0;
      if (ad < bd) return order === "asc" ? -1 : 1;
      if (ad > bd) return order === "asc" ? 1 : -1;
      return 0;
    }
    if (orderBy === "User ID") {
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return order === "asc" ? aNum - bNum : bNum - aNum;
      }
    }
    const aStr = aVal == null ? "" : String(aVal).toLowerCase();
    const bStr = bVal == null ? "" : String(bVal).toLowerCase();
    if (aStr < bStr) return order === "asc" ? -1 : 1;
    if (aStr > bStr) return order === "asc" ? 1 : -1;
    return 0;
  });

  // Export CSV — uses visibleColumns (labels == backend headers here)
  const handleExport = async () => {
    try {
      setExporting(true);
      const token = getToken();

      const payload: any = {
        ...buildFlatPayload({ limit: "all", forExport: true }),
        columns: visibleColumns, // backend expects these headers (labels == values)
      };

      if (orderBy === "User ID") {
        payload.sort_dir = order;
      }

      const response = await exportRetailersAndAccounts_csv_v1(
        payload,
        token,
        true
      );

      if (response instanceof Blob) {
        const blobType = response.type || "text/csv";
        if (blobType.includes("csv") || blobType.includes("text")) {
          const url = window.URL.createObjectURL(response);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `master_outlet_list_${new Date().toISOString().slice(0, 10)}.csv`
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          return;
        }

        // excel blob -> convert first sheet to CSV
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[firstSheetName]);

        const csvBlob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(csvBlob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `master_outlet_list_${new Date().toISOString().slice(0, 10)}.csv`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const csvBlob = new Blob([JSON.stringify(response, null, 2)], {
          type: "application/json",
        });
        const url = window.URL.createObjectURL(csvBlob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `master_outlet_list_${new Date().toISOString().slice(0, 10)}.json`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  // 24h, e.g. "10-Oct-2025 14:23"
  const formatDisplayDate = (val: any) => {
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);

    const datePart = d
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-"); // "10 Oct 2025" -> "10-Oct-2025"

    const timePart = d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${datePart} ${timePart}`;
  };

  const formatCell = (header: string, raw: any) => {
    if (raw === null || raw === undefined || raw === "") return "-";

    // Date-like columns
    if (
      header === "Last Updated On" ||
      header === "Contracted Since" ||
      header.toLowerCase().includes("date") ||
      header.toLowerCase().includes("since")
    ) {
      return formatDisplayDate(raw);
    }

    return String(raw);
  };

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "10px",
        border: "1px solid #cfcfcf",
        margin: "30px 32px 100px",
      }}
    >
      {/* Header */}
      <Box sx={{ padding: "25px", display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "16px", fontWeight: "600", marginTop: "10px" }}>
          Total {totalRows} Users
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center" }}>
          {/* Rewards-style Column Picker with tooltip & search */}
          <Box sx={{ marginRight: "20px" }}>
            <Tooltip
              title={
                tempSelectedColumns.length
                  ? tempSelectedColumns.join(", ")
                  : "Select Columns"
              }
              placement="top"
              arrow
              PopperProps={{
                modifiers: [{ name: "offset", options: { offset: [0, 10] } }],
              }}
            >
              <FormControl sx={{ minWidth: 240, width: 240 }}>
                <Select
                  multiple
                  displayEmpty
                  value={tempSelectedColumns}
                  open={colSelectOpen}
                  onOpen={() => setColSelectOpen(true)}
                  onClose={() => {
                    setColSelectOpen(false);
                    const next = withDefaultsIfEmpty(tempSelectedColumns);
                    setTempSelectedColumns(next); // keep internal state valid
                    setVisibleColumns(next); // commit to table
                    setColSearch("");
                  }}
                  onChange={(e) => {
                    const val = e.target.value;
                    const arr =
                      typeof val === "string"
                        ? (val as string).split(",")
                        : (val as string[]);
                    if (Array.isArray(arr) && (arr as any[]).includes("__ALL__")) {
                      // handled by MenuItem onClick but keep safety
                      toggleSelectAllOnFiltered();
                      return;
                    }
                    const next = withDefaultsIfEmpty(
                      Array.isArray(arr) ? (arr as string[]) : []
                    );
                    setTempSelectedColumns(next);
                  }}
                  renderValue={(selected) => {
                    if ((selected as string[]).length === 0) return "Select Columns";
                    return (selected as string[]).join(", ");
                  }}
                  inputProps={{ "aria-label": "columns-select" }}
                  IconComponent={() => (
                    <CustomArrow
                      open={colSelectOpen}
                      onClick={() => setColSelectOpen((v) => !v)}
                    />
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 240,
                        paddingTop: 0,
                        paddingBottom: 0,
                        overflow: "overlay",
                        borderRadius: 10,
                        background: "#fff",
                        boxShadow: "none",
                      },
                    },
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    disableAutoFocusItem: true,
                    MenuListProps: {
                      style: {
                        paddingTop: 0,
                        paddingBottom: 0,
                        maxHeight: "250px",
                        overflowY: "auto",
                      },
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                          setTimeout(() => colSearchRef.current?.focus(), 0);
                        }
                      },
                    },
                  }}
                  sx={{
                    minWidth: 240,
                    height: "40px",
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    marginRight: "8px",
                    width: "240px",
                    position: "relative",
                    "& .MuiSelect-icon": { display: "none" },
                    "&:hover, &.Mui-focused, &.Mui-focusVisible": {
                      background: "transparent !important",
                      boxShadow: "none !important",
                      outline: "none !important",
                    },
                  }}
                >
                  <ListSubheader
                    sx={{
                      bgcolor: "white",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        px: 1,
                        py: 0.5,
                        border: "1px solid #ccc",
                        borderRadius: 1,
                        bgcolor: "transparent",
                      }}
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <input
                        ref={colSearchRef}
                        value={colSearch}
                        onChange={(e) => setColSearch(e.target.value)}
                        placeholder="Search columns..."
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          fontSize: 14,
                          background: "transparent",
                          padding: "4px",
                          height: "40px",
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          (e.nativeEvent as KeyboardEvent).stopImmediatePropagation?.();
                        }}
                      />
                      {colSearch && (
                        <Box
                          component="span"
                          sx={{
                            cursor: "pointer",
                            fontWeight: "bold",
                            fontSize: 18,
                            lineHeight: 1,
                            px: 1,
                          }}
                          onClick={(ev) => {
                            ev.stopPropagation();
                            setColSearch("");
                            setTimeout(() => colSearchRef.current?.focus(), 0);
                          }}
                        >
                          ×
                        </Box>
                      )}
                    </Box>
                  </ListSubheader>

                  <MenuItem
                    dense
                    value="__ALL__"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      toggleSelectAllOnFiltered();
                    }}
                  >
                    <Checkbox checked={allFilteredSelected} />
                    <ListItemText primary="Select All" />
                  </MenuItem>

                  {filteredColumns.length ? (
                    filteredColumns.map((name) => (
                      <MenuItem key={name} value={name}>
                        <Checkbox checked={tempSelectedColumns.includes(name)} />
                        <ListItemText primary={name} />
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled dense>
                      <ListItemText primary="No results" />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Tooltip>
          </Box>

          <Button
            variant="contained"
            disabled={exporting}
            sx={{
              textTransform: "capitalize",
              backgroundColor: "#fff",
              fontSize: "14px",
              fontWeight: "500",
              height: "40px",
              padding: "16px",
              borderRadius: "10px",
              color: "#C8172F",
              border: "1px solid #C8172F",
              boxShadow: "none",
            }}
            startIcon={<img src={Export} alt="export" />}
            onClick={handleExport}
          >
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          borderTop: "1px solid #d3d3d3",
          boxShadow: "none",
          maxWidth: "100%",
          overflowX: "auto",
        }}
      >
        <Table sx={{ minWidth: visibleColumns.length * 150, tableLayout: "fixed" }}>
          <TableHead sx={{ background: "#EEF4FF" }}>
            <TableRow>
              {visibleColumns.map((label) => (
                <TableCell
                  key={label}
                  sx={{
                    minWidth: 150,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 300,
                  }}
                >
                  <TableSortLabel
                    active={orderBy === label}
                    direction={orderBy === label ? order : "asc"}
                    onClick={() => handleSort(label)}
                    IconComponent={() => (
                      <img src={Sorting} alt="sort" style={{ width: 16, height: 16 }} />
                    )}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedRows.length > 0 ? (
              sortedRows.map((row, idx) => (
                <TableRow
                  key={row["User ID"] ?? `${row["Outlet Salesforce ID"] ?? ""}-${idx}`}
                >
                  {visibleColumns.map((label) => {
                    const raw = row[label];
                    const display = formatCell(label, raw);
                    return (
                      <TableCell
                        key={label}
                        title={typeof display === "string" ? display : undefined}
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 300,
                          minWidth: 150,
                        }}
                      >
                        {display}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} align="center">
                  {isLoading ? "Loading..." : "No data available"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination + rows-per-page */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
        }}
      >
        <Box sx={{ width: 200 }} />
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <PaginationReports
            page={page}
            limit={itemsPerPage}
            totalPages={totalPages}
            setPage={setPage}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ color: "#555" }}>
            Rows per page
          </Typography>
          <FormControl size="small" sx={{ minWidth: 92 }}>
            <Select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setPage(1); // reset to first page on size change
              }}
              sx={{ borderRadius: "8px", height: 36 }}
            >
              {[10, 25, 50, 100, 200].map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default ReportMasterTrTable;
